import { useEffect, useState } from 'react';
import { fetchProfile } from '../api/profileApi';
import { getStoredLoginIdentifier, getSessionToken } from '../auth/sessionToken';
import { useAuth } from '../auth/AuthContext';
import { extractProfileDisplaySummary } from '../utils/profileDisplaySummary';
import { resolveProfileAvatarDisplay } from '../utils/profileAvatarDisplay';
import {
  cacheProfileAvatarFromServer,
  loadLocalProfileAvatarSrc,
  PROFILE_AVATAR_UPDATED_EVENT,
  syncLocalProfileAvatarWithServer,
} from '../utils/profileAvatarStorage';

export function useProfileAvatarSrc(): string {
  const { isLoggedIn, userName } = useAuth();
  const [avatarSrc, setAvatarSrc] = useState(() => resolveProfileAvatarDisplay(null));

  useEffect(() => {
    const refresh = () => {
      setAvatarSrc(resolveProfileAvatarDisplay(null));
    };
    window.addEventListener(PROFILE_AVATAR_UPDATED_EVENT, refresh);
    refresh();
    return () => window.removeEventListener(PROFILE_AVATAR_UPDATED_EVENT, refresh);
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setAvatarSrc(resolveProfileAvatarDisplay(null));
      return;
    }
    let cancelled = false;
    void fetchProfile()
      .then((data) => {
        if (cancelled) {
          return;
        }
        const summary = extractProfileDisplaySummary(
          data,
          userName,
          getStoredLoginIdentifier(),
          getSessionToken(),
        );
        if (summary.avatarSrc) {
          syncLocalProfileAvatarWithServer(summary.avatarSrc);
        }
        setAvatarSrc(resolveProfileAvatarDisplay(summary.avatarSrc));
      })
      .catch(() => {
        if (!cancelled) {
          setAvatarSrc(resolveProfileAvatarDisplay(null));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn, userName]);

  return avatarSrc;
}
